/**
 * Vendor Import — Review step.
 *
 * Shows the engine's proposed property mappings grouped by entity type, with
 * a per-row override between mapping to an existing property, creating a new
 * one, or skipping it. Presentational only — the wizard owns fetching the
 * proposals and holding the override state.
 */

import { EmptyState } from '@/lib/base/empty-state';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';

import { LockedMappingsTable } from './LockedMappingsTable';
import { MappingReviewSection } from './MappingReviewSection';
import type { CoreMapping, MappingAction, MappingProposal } from './types';

// eslint-disable-next-line react-refresh/only-export-components -- shared helper re-exported for consumers
export { reviewRowKey } from './reviewGrouping';

export interface MappingReviewStepProps {
  /** null when the proposals request failed to load. */
  proposalsByEntity: Record<string, MappingProposal[]> | null;
  loadError: boolean;
  /**
   * Per-entity-type flag: true when the backend capped that entity type's
   * proposals and dropped the rest. null when proposals haven't loaded.
   */
  truncatedByEntity: Record<string, boolean> | null;
  /**
   * Records scanned per entity type — the denominator for each proposal's
   * populated_count. null when proposals haven't loaded.
   */
  totalRecordsByEntity: Record<string, number> | null;
  /** Keyed by reviewRowKey(entity_type_name, raw_key). */
  actionByKey: Record<string, MappingAction>;
  onActionChange: (key: string, action: MappingAction) => void;
  /**
   * Fields the engine mapped to core (system) properties, rendered as a
   * non-editable "locked" table above the editable proposals for that entity
   * type. Optional and absent for providers that don't send core mappings
   * (e.g. Lightfield, Attio) — null/undefined renders no locked sections.
   */
  coreMappingsByEntity?: Record<string, CoreMapping[]> | null;
  /**
   * Optional label resolver for entity types the built-in registry doesn't
   * know — e.g. vendor-discovered custom object types the user named and
   * selected. Returning undefined falls back to `getTypeLabelByName`, which
   * strips the `org_` prefix and title-cases the slug (so `org_widget` renders
   * as "Widget", or "Widgets" with `plural`). `plural` selects the plural form
   * for that fallback. Supplying a resolver matters when the discovered label
   * differs from that slug-derived fallback — e.g. `org_widget` whose vendor
   * label is "Sales Widget" resolves here to "Sales Widget" instead of "Widget".
   */
  resolveEntityLabel?: (
    entityType: string,
    options: { plural: boolean }
  ) => string | undefined;
}

/**
 * Resolve an entity type's display label, preferring a caller-supplied resolver
 * (discovered custom-type labels) over the built-in registry / slug fallback.
 */
function resolveTypeLabel(
  entityType: string,
  options: { plural: boolean },
  resolveEntityLabel: MappingReviewStepProps['resolveEntityLabel']
): string {
  return (
    resolveEntityLabel?.(entityType, options) ??
    getTypeLabelByName(entityType, { plural: options.plural })
  );
}

export function MappingReviewStep({
  proposalsByEntity,
  loadError,
  truncatedByEntity,
  totalRecordsByEntity,
  actionByKey,
  onActionChange,
  coreMappingsByEntity,
  resolveEntityLabel,
}: MappingReviewStepProps) {
  if (loadError) {
    return (
      <EmptyState
        title="We couldn't load the proposed property mappings."
        description="You can continue and we'll map properties automatically."
      />
    );
  }

  const sections = proposalsByEntity
    ? Object.entries(proposalsByEntity).filter(
        ([, proposals]) => proposals.length > 0
      )
    : [];

  // Entity types the engine returned no proposal rows for. Surfaced as a note
  // so a selected type (e.g. Deals) never silently vanishes from this step, but
  // split by *why* it's empty so the note is accurate:
  //   - fullyMapped: the type has records and every field maps to a system
  //     property automatically.
  //   - noRecords: the type had zero source records to scan, so there was
  //     nothing to map at all. Reporting these as "fully mapped" (as this once
  //     did) misleads the user into thinking data was imported.
  // We only treat a type as "no records" when totalRecordsByEntity explicitly
  // reports 0; an absent/unknown count keeps the prior fully-mapped default.
  const emptyTypes = proposalsByEntity
    ? Object.entries(proposalsByEntity)
        .filter(([, proposals]) => proposals.length === 0)
        .map(([entityType]) => entityType)
    : [];
  const noRecordTypes = emptyTypes.filter(
    entityType => totalRecordsByEntity?.[entityType] === 0
  );
  const fullyMappedTypes = emptyTypes.filter(
    entityType => totalRecordsByEntity?.[entityType] !== 0
  );

  // Entity types with locked core-field mappings but no editable proposals —
  // these still need their own section since they're not in `sections`
  // (which filters to proposals.length > 0).
  const coreEntityTypes = coreMappingsByEntity
    ? Object.entries(coreMappingsByEntity)
        .filter(([, mappings]) => mappings.length > 0)
        .map(([entityType]) => entityType)
    : [];
  const sectionEntityTypes = new Set(
    sections.map(([entityType]) => entityType)
  );
  const standaloneLockedTypes = coreEntityTypes.filter(
    entityType => !sectionEntityTypes.has(entityType)
  );

  if (
    sections.length === 0 &&
    fullyMappedTypes.length === 0 &&
    noRecordTypes.length === 0 &&
    standaloneLockedTypes.length === 0
  ) {
    return (
      <EmptyState
        title='No custom properties to review.'
        description='No properties need mapping for the selected record types.'
      />
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      {sections.map(([entityType, proposals]) => {
        const entityLabel = resolveTypeLabel(
          entityType,
          { plural: true },
          resolveEntityLabel
        );
        const isTruncated = truncatedByEntity?.[entityType] ?? false;
        const coreMappings = coreMappingsByEntity?.[entityType] ?? [];
        return (
          <MappingReviewSection
            key={entityType}
            entityType={entityType}
            entityLabel={entityLabel}
            proposals={proposals}
            coreMappings={coreMappings}
            totalRecords={totalRecordsByEntity?.[entityType]}
            isTruncated={isTruncated}
            actionByKey={actionByKey}
            onActionChange={onActionChange}
          />
        );
      })}
      {standaloneLockedTypes.map(entityType => {
        const entityLabel = resolveTypeLabel(
          entityType,
          { plural: true },
          resolveEntityLabel
        );
        const coreMappings = coreMappingsByEntity?.[entityType] ?? [];
        return (
          <div key={entityType} className='flex flex-col gap-2'>
            <h3 className='text-sm font-semibold text-primary'>
              {entityLabel}
            </h3>
            <LockedMappingsTable
              entityType={entityType}
              entityLabel={entityLabel}
              mappings={coreMappings}
            />
          </div>
        );
      })}
      {fullyMappedTypes.length > 0 && (
        <div className='flex flex-col gap-1'>
          {fullyMappedTypes.map(entityType => (
            <p key={entityType} className='text-sm text-tertiary'>
              All{' '}
              {resolveTypeLabel(
                entityType,
                { plural: false },
                resolveEntityLabel
              ).toLowerCase()}{' '}
              properties were mapped to system properties.
            </p>
          ))}
        </div>
      )}
      {noRecordTypes.length > 0 && (
        <div className='flex flex-col gap-1'>
          {noRecordTypes.map(entityType => (
            <p key={entityType} className='text-sm text-tertiary'>
              No{' '}
              {resolveTypeLabel(
                entityType,
                { plural: true },
                resolveEntityLabel
              ).toLowerCase()}{' '}
              were found to import.
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
